import React from "react";

type DiffToken = {
  value: string;
  type: "same" | "added" | "removed";
};

type NarrativeDiffTextProps = {
  original?: string;
  revised?: string;
};

const tokenize = (value: string): string[] =>
  value.match(/\w+|[^\w\s]+|\s+/g) ?? [];

const diffTokens = (original: string, revised: string): DiffToken[] => {
  // Keep whitespace as tokens so the rendered suggestion preserves the original spacing.
  const a = tokenize(original);
  const b = tokenize(revised);

  // Build an LCS table. table[i][j] is the longest shared token sequence
  // between original.slice(i) and revised.slice(j).
  const table: number[][] = Array.from({ length: a.length + 1 }, () =>
    Array(b.length + 1).fill(0)
  );

  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      table[i][j] = a[i] === b[j] ? table[i + 1][j + 1] + 1 : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }

  const output: DiffToken[] = [];
  let i = 0;
  let j = 0;

  // Walk the table forward to label tokens as unchanged, removed, or added.
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      output.push({ value: a[i], type: "same" });
      i += 1;
      j += 1;
    } else if (table[i + 1][j] >= table[i][j + 1]) {
      output.push({ value: a[i], type: "removed" });
      i += 1;
    } else {
      output.push({ value: b[j], type: "added" });
      j += 1;
    }
  }

  while (i < a.length) {
    output.push({ value: a[i], type: "removed" });
    i += 1;
  }

  while (j < b.length) {
    output.push({ value: b[j], type: "added" });
    j += 1;
  }

  return output;
};

export const NarrativeDiffText = ({ original = "", revised = "" }: NarrativeDiffTextProps) => {
  if (!original) {
    return <>{revised}</>;
  }

  if (!revised) {
    return (
      <span className="narrative-ocg-diff narrative-ocg-diff--removed">
        {original}
      </span>
    );
  }

  const tokens = diffTokens(original, revised);
  const isFullSentenceReplacement = !tokens.some((token) => token.type === "same" && token.value.trim());

  if (isFullSentenceReplacement) {
    return <span style={{ color: "#333333" }}>{revised}</span>;
  }

  return (
    <>
      {tokens.map((token, index) => {
        if (token.type === "removed") {
          return (
            <span className="narrative-ocg-diff narrative-ocg-diff--removed" key={`${token.type}-${index}`}>
              {token.value}
            </span>
          );
        }

        if (token.type === "added") {
          return (
            <span className="narrative-ocg-diff narrative-ocg-diff--added" key={`${token.type}-${index}`}>
              {token.value}
            </span>
          );
        }

        return <React.Fragment key={`${token.type}-${index}`}>{token.value}</React.Fragment>;
      })}
    </>
  );
};
